import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { SharedNavbar } from '@/components/SharedNavbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PageTransition } from '@/components/PageTransition';
import {
  Plus,
  MoreVertical,
  Trash2,
  Edit,
  ClipboardList,
  CheckSquare,
  Copy,
  FileText,
  Search,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ArrowLeft,
  List,
} from 'lucide-react';
import { TOOL_REGISTRY as toolConfigs } from '@/config/tools';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Sortable table header
const SortableHeader = ({ label, sortKey, currentSort, onSort }) => {
  const isSorted = currentSort.key === sortKey;
  const direction = currentSort.direction;

  return (
    <button
      onClick={() => onSort(sortKey)}
      className="flex items-center gap-1 hover:text-gray-900 transition-colors"
    >
      {label}
      {isSorted ? (
        direction === 'asc' ? (
          <ChevronUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )
      ) : (
        <ChevronsUpDown className="h-3.5 w-3.5 text-gray-400" />
      )}
    </button>
  );
};

export default function ChecklistTemplatesPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { userProfile, company } = useAuth();
  usePageTitle('Checklist Templates');

  // State
  const [initialLoading, setInitialLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [space, setSpace] = useState(null);
  const [spaceTools, setSpaceTools] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_date', direction: 'desc' });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isManageItemsDialogOpen, setIsManageItemsDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateItems, setTemplateItems] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [newItemTitle, setNewItemTitle] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [editingItem, setEditingItem] = useState(null);

  // Determine scope: project-level or company-level
  const isProjectLevel = !!projectId;

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch space data
  useEffect(() => {
    const fetchSpaceData = async () => {
      if (!projectId) {
        setInitialLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('id', projectId)
          .single();

        if (!error && data) {
          setSpace(data);
          if (data.tools) {
            const toolsObj = typeof data.tools === 'string' ? JSON.parse(data.tools) : data.tools;
            const tools = Object.entries(toolsObj)
              .filter(([key, enabled]) => enabled === true)
              .map(([toolKey]) => ({
                id: toolKey,
                tool_type: toolKey,
                name: toolConfigs[toolKey]?.name || toolKey,
                icon: toolConfigs[toolKey]?.icon,
                color: toolConfigs[toolKey]?.color
              }));
            setSpaceTools(tools);
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchSpaceData();
  }, [projectId]);

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      let query = supabase
        .from('checklist_templates')
        .select(`
          *,
          items:checklist_template_items(count),
          creator:created_by(id, first_name, last_name, display_name)
        `)
        .eq('company_id', company.id)
        .eq('is_archived', false);

      // Filter by project or company level
      if (isProjectLevel) {
        query = query.eq('project_id', projectId);
      } else {
        query = query.is('project_id', null);
      }

      // Apply search
      if (debouncedSearch) {
        query = query.or(`name.ilike.%${debouncedSearch}%,description.ilike.%${debouncedSearch}%`);
      }

      // Apply sorting
      const sortColumn = sortConfig.key === 'items' ? 'created_date' : sortConfig.key;
      query = query.order(sortColumn, { ascending: sortConfig.direction === 'asc' });

      const { data, error } = await query;

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (company?.id) {
      fetchTemplates();
    }
  }, [company?.id, projectId, debouncedSearch, sortConfig]);

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Create template
  const createTemplate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    try {
      const newTemplate = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        company_id: company.id,
        project_id: isProjectLevel ? projectId : null,
        created_by: userProfile.id,
      };

      const { data, error } = await supabase
        .from('checklist_templates')
        .insert([newTemplate])
        .select()
        .single();

      if (error) throw error;

      await fetchTemplates();
      setFormData({ name: '', description: '' });
      setIsCreateDialogOpen(false);
      toast.success('Template created!');
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
    }
  };

  // Update template
  const updateTemplate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    try {
      const { error } = await supabase
        .from('checklist_templates')
        .update({
          name: formData.name.trim(),
          description: formData.description.trim() || null,
        })
        .eq('id', selectedTemplate.id);

      if (error) throw error;

      await fetchTemplates();
      setFormData({ name: '', description: '' });
      setIsEditDialogOpen(false);
      setSelectedTemplate(null);
      toast.success('Template updated!');
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Failed to update template');
    }
  };

  // Delete template
  const deleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template? This cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('checklist_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;

      await fetchTemplates();
      toast.success('Template deleted');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  // Duplicate template
  const duplicateTemplate = async (template) => {
    try {
      // Create new template
      const { data: newTemplate, error: templateError } = await supabase
        .from('checklist_templates')
        .insert([{
          name: `${template.name} (Copy)`,
          description: template.description,
          company_id: company.id,
          project_id: isProjectLevel ? projectId : null,
          created_by: userProfile.id,
        }])
        .select()
        .single();

      if (templateError) throw templateError;

      // Fetch items from original template
      const { data: originalItems, error: itemsError } = await supabase
        .from('checklist_template_items')
        .select('*')
        .eq('template_id', template.id)
        .order('order_index');

      if (itemsError) throw itemsError;

      // Copy items to new template
      if (originalItems && originalItems.length > 0) {
        const newItems = originalItems.map(item => ({
          template_id: newTemplate.id,
          title: item.title,
          description: item.description,
          order_index: item.order_index,
        }));

        const { error: copyError } = await supabase
          .from('checklist_template_items')
          .insert(newItems);

        if (copyError) throw copyError;
      }

      await fetchTemplates();
      toast.success('Template duplicated!');
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error('Failed to duplicate template');
    }
  };

  // Open edit dialog
  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
    });
    setIsEditDialogOpen(true);
  };

  // Open manage items dialog
  const openManageItemsDialog = async (template) => {
    setSelectedTemplate(template);
    setIsManageItemsDialogOpen(true);

    // Fetch items
    try {
      const { data, error } = await supabase
        .from('checklist_template_items')
        .select('*')
        .eq('template_id', template.id)
        .order('order_index');

      if (error) throw error;
      setTemplateItems(data || []);
    } catch (error) {
      console.error('Error fetching template items:', error);
      toast.error('Failed to load template items');
    }
  };

  // Add template item
  const addTemplateItem = async () => {
    if (!newItemTitle.trim()) {
      toast.error('Please enter an item title');
      return;
    }

    try {
      const newItem = {
        template_id: selectedTemplate.id,
        title: newItemTitle.trim(),
        description: newItemDescription.trim() || null,
        order_index: templateItems.length,
      };

      const { data, error } = await supabase
        .from('checklist_template_items')
        .insert([newItem])
        .select()
        .single();

      if (error) throw error;

      setTemplateItems([...templateItems, data]);
      setNewItemTitle('');
      setNewItemDescription('');
      toast.success('Item added!');
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Failed to add item');
    }
  };

  // Update template item
  const updateTemplateItem = async (item) => {
    if (!item.title.trim()) {
      toast.error('Item title cannot be empty');
      return;
    }

    try {
      const { error } = await supabase
        .from('checklist_template_items')
        .update({
          title: item.title.trim(),
          description: item.description?.trim() || null,
        })
        .eq('id', item.id);

      if (error) throw error;

      setTemplateItems(templateItems.map(i => i.id === item.id ? item : i));
      setEditingItem(null);
      toast.success('Item updated!');
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Failed to update item');
    }
  };

  // Delete template item
  const deleteTemplateItem = async (itemId) => {
    if (!confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('checklist_template_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      setTemplateItems(templateItems.filter(i => i.id !== itemId));
      toast.success('Item deleted');
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };

  // Navigate back to checklists
  const goBackToChecklists = () => {
    if (isProjectLevel) {
      navigate(`/space/${projectId}/checklists`);
    } else {
      navigate('/checklists');
    }
  };

  return (
    <PageTransition>
      <AppShell3 navbar={<SharedNavbar />} sidebar={null}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <ToolHeader
            spaceName={space?.name}
            spaceImage={space?.image}
            toolName="Checklist Templates"
            currentTool="checklists"
            spaceTools={spaceTools}
            actions={
              !initialLoading && (
                <div className="flex items-center gap-2">
                  {/* Back Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goBackToChecklists}
                    className="h-8 text-sm"
                  >
                    <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
                    Back to Checklists
                  </Button>

                  {/* Search */}
                  <div className="relative w-[300px]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <Input
                      placeholder="Search templates..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 h-8 text-sm"
                    />
                  </div>

                  {/* Refresh */}
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={fetchTemplates}
                    className="h-8 w-8"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>

                  {/* New Template */}
                  <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="h-8 text-sm">
                        <Plus className="h-3.5 w-3.5 mr-1.5" />
                        New Template
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create Template</DialogTitle>
                        <DialogDescription>
                          Create a new checklist template that can be reused
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div>
                          <label className="text-sm font-medium mb-1 block">Template Name</label>
                          <Input
                            placeholder="e.g., IT Onboarding"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-1 block">Description (optional)</label>
                          <Textarea
                            placeholder="Describe what this template is for..."
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                          />
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button onClick={createTemplate} className="flex-1">Create Template</Button>
                          <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )
            }
          />

          {/* Main Content */}
          <div className="flex-1 overflow-auto p-6">
            {initialLoading ? (
              <div className="flex items-center justify-center h-full">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="max-w-[1400px] mx-auto space-y-6">
                {/* Table */}
                <div className="bg-white rounded-lg border border-gray-200">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          <SortableHeader
                            label="Template Name"
                            sortKey="name"
                            currentSort={sortConfig}
                            onSort={handleSort}
                          />
                        </TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>
                          <SortableHeader
                            label="Items"
                            sortKey="items"
                            currentSort={sortConfig}
                            onSort={handleSort}
                          />
                        </TableHead>
                        <TableHead>Created By</TableHead>
                        <TableHead>
                          <SortableHeader
                            label="Created Date"
                            sortKey="created_date"
                            currentSort={sortConfig}
                            onSort={handleSort}
                          />
                        </TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {templates.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-12">
                            <div className="flex flex-col items-center gap-2 text-gray-500">
                              <ClipboardList className="h-12 w-12 text-gray-300" />
                              <p className="text-sm font-medium">No templates found</p>
                              <p className="text-xs">
                                {searchTerm
                                  ? 'Try adjusting your search'
                                  : 'Create your first template to get started'}
                              </p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        templates.map((template) => (
                          <TableRow
                            key={template.id}
                            className="cursor-pointer"
                            onClick={() => openManageItemsDialog(template)}
                          >
                            {/* Template Name */}
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-900">
                                  {template.name}
                                </span>
                              </div>
                            </TableCell>

                            {/* Description */}
                            <TableCell>
                              <span className="text-sm text-gray-600 line-clamp-2 max-w-[300px]">
                                {template.description || '-'}
                              </span>
                            </TableCell>

                            {/* Items Count */}
                            <TableCell>
                              <div className="flex items-center gap-1.5 text-sm text-gray-700">
                                <CheckSquare className="h-3.5 w-3.5 text-gray-400" />
                                {template.items?.[0]?.count || 0} items
                              </div>
                            </TableCell>

                            {/* Created By */}
                            <TableCell>
                              <span className="text-sm text-gray-600">
                                {template.creator?.display_name ||
                                 `${template.creator?.first_name || ''} ${template.creator?.last_name || ''}`.trim() ||
                                 'Unknown'}
                              </span>
                            </TableCell>

                            {/* Created Date */}
                            <TableCell>
                              <span className="text-sm text-gray-600">
                                {format(new Date(template.created_date), 'dd MMM yyyy')}
                              </span>
                            </TableCell>

                            {/* Actions */}
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                  <DropdownMenuItem onClick={() => openManageItemsDialog(template)}>
                                    <FileText className="h-3.5 w-3.5 mr-2" />
                                    Manage Items
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openEditDialog(template)}>
                                    <Edit className="h-3.5 w-3.5 mr-2" />
                                    Edit Template
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => duplicateTemplate(template)}>
                                    <Copy className="h-3.5 w-3.5 mr-2" />
                                    Duplicate
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => deleteTemplate(template.id)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="h-3.5 w-3.5 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Edit Template Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Template</DialogTitle>
              <DialogDescription>
                Update your template details
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Template Name</label>
                <Input
                  placeholder="e.g., IT Onboarding"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Description (optional)</label>
                <Textarea
                  placeholder="Describe what this template is for..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={updateTemplate} className="flex-1">Save Changes</Button>
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Manage Template Items Dialog */}
        <Dialog open={isManageItemsDialogOpen} onOpenChange={setIsManageItemsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden p-0">
            <div className="overflow-y-auto max-h-[80vh] p-6">
            <DialogHeader>
              <DialogTitle>{selectedTemplate?.name} - Items</DialogTitle>
              <DialogDescription>
                Manage the checklist items for this template
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {/* Items List */}
              <div className="space-y-2">
                {templateItems.map((item, index) => (
                  <div key={item.id} className="flex items-start gap-2 p-3 border rounded-lg">
                    <div className="flex-1 min-w-0">
                      {editingItem === item.id ? (
                        <div className="space-y-2">
                          <Input
                            value={item.title}
                            onChange={(e) => setTemplateItems(templateItems.map(i =>
                              i.id === item.id ? { ...i, title: e.target.value } : i
                            ))}
                            placeholder="Item title"
                          />
                          <Textarea
                            value={item.description || ''}
                            onChange={(e) => setTemplateItems(templateItems.map(i =>
                              i.id === item.id ? { ...i, description: e.target.value } : i
                            ))}
                            placeholder="Item description (optional)"
                            rows={2}
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => updateTemplateItem(item)}>Save</Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingItem(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="font-medium">{index + 1}. {item.title}</div>
                          {item.description && (
                            <div className="text-sm text-muted-foreground mt-1">{item.description}</div>
                          )}
                        </>
                      )}
                    </div>
                    {editingItem !== item.id && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingItem(item.id)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteTemplateItem(item.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}

                {templateItems.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No items yet. Add your first item below.
                  </div>
                )}
              </div>

              {/* Add New Item */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium mb-2">Add New Item</h4>
                <div className="space-y-2">
                  <Input
                    placeholder="Item title"
                    value={newItemTitle}
                    onChange={(e) => setNewItemTitle(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addTemplateItem()}
                  />
                  <Textarea
                    placeholder="Item description (optional)"
                    value={newItemDescription}
                    onChange={(e) => setNewItemDescription(e.target.value)}
                    rows={2}
                  />
                  <Button onClick={addTemplateItem} size="sm" className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </div>
              </div>
            </div>
            </div>
          </DialogContent>
        </Dialog>
      </AppShell3>
    </PageTransition>
  );
}
